import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RefreshCw, Trash2, Youtube, Link2, Unlink, Cloud, Key, Save, X as XIcon, Download, Upload } from "lucide-react";
import { Api, type Source } from "../lib/api.js";
import { getBearer } from "../lib/auth.js";

type Kind = Source["kind"];
const KINDS: { value: Kind; label: string }[] = [
  { value: "m3u",     label: "M3U playlist" },
  { value: "xmltv",   label: "XMLTV guide" },
  { value: "youtube", label: "YouTube" },
  { value: "xtream",  label: "Xtream Codes" },
];

export function Sources(): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["sources"], queryFn: Api.sources.list });

  const [kind, setKind] = useState<Kind>("m3u");
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");

  // YouTube-specific config
  const [ytQueries, setYtQueries] = useState("");
  const [ytChannelIds, setYtChannelIds] = useState("");
  const [ytMaxPerQuery, setYtMaxPerQuery] = useState(25);
  const [ytOrder, setYtOrder] = useState<"date" | "relevance" | "viewCount">("date");

  const create = useMutation({
    mutationFn: async () => {
      const base: { kind: Kind; label: string; url?: string; config?: Record<string, unknown> } = {
        kind,
        label: label.trim(),
      };
      if (kind === "youtube") {
        const queries = ytQueries.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
        const channelIds = ytChannelIds.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
        if (queries.length === 0 && channelIds.length === 0) {
          throw new Error("Add at least one query or channel ID");
        }
        base.config = {
          queries: queries.length > 0 ? queries : undefined,
          channelIds: channelIds.length > 0 ? channelIds : undefined,
          maxPerQuery: ytMaxPerQuery,
          order: ytOrder,
        };
      } else if (url.trim().length > 0) {
        base.url = url.trim();
      }
      return Api.sources.create(base as { kind: Kind; label: string });
    },
    onSuccess: () => {
      setLabel("");
      setUrl("");
      setYtQueries("");
      setYtChannelIds("");
      void qc.invalidateQueries({ queryKey: ["sources"] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: number) => Api.sources.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sources"] }),
  });
  const refresh = useMutation({
    mutationFn: (id: number) => Api.sources.refresh(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sources"] }),
  });

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl text-ivory">Sources</h1>
        <span className="section-label">{data?.sources.length ?? 0} configured</span>
      </div>
      <div className="ornament-rule" />

      <YouTubeAccountPanel />
      <RealDebridPanel />
      <CredentialsPanel />
      <ConfigPanel />

      <form
        className="card p-5 space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (label.trim().length > 0 && !create.isPending) create.mutate();
        }}
      >
        <div className="grid grid-cols-[160px_1fr] gap-4">
          <div>
            <label className="section-label">Kind</label>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as Kind)}
              className="mt-2 w-full bg-ink/60 border border-hairline rounded-md px-3 py-2 text-ivory focus:border-gold outline-none"
            >
              {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
          </div>
          <div>
            <label className="section-label">Label</label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              autoComplete="off"
              className="mt-2 w-full bg-ink/60 border border-hairline rounded-md px-3 py-2 text-ivory focus:border-gold outline-none"
              placeholder={kind === "youtube" ? "YouTube — long-form history" : "House M3U"}
            />
          </div>
        </div>

        {kind === "youtube" ? (
          <YouTubeFields
            queries={ytQueries} setQueries={setYtQueries}
            channelIds={ytChannelIds} setChannelIds={setYtChannelIds}
            maxPerQuery={ytMaxPerQuery} setMaxPerQuery={setYtMaxPerQuery}
            order={ytOrder} setOrder={setYtOrder}
          />
        ) : (
          <div>
            <label className="section-label">URL</label>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              autoComplete="off"
              className="mt-2 w-full bg-ink/60 border border-hairline rounded-md px-3 py-2 text-ivory focus:border-gold outline-none font-mono text-sm"
              placeholder={
                kind === "m3u"   ? "https://provider.example/api/list/USER/PASS/m3u8/livetv" :
                kind === "xmltv" ? "https://provider.example/api/xmltv/USER/PASS" :
                                   "https://provider.example/player_api.php"
              }
            />
            {kind === "m3u" && (
              <div className="mt-2 text-xs text-parchment/70">
                If your provider's URL only resolves on their private DNS, save the M3U file from VLC
                and use <code className="text-gold">file:///media/shared/For Claude/yourfile.m3u</code>.
              </div>
            )}
          </div>
        )}

        {create.isError && (
          <div className="text-vermilionHi text-sm">{(create.error as Error).message}</div>
        )}

        <div className="flex justify-end">
          <button type="submit" className="pill" disabled={create.isPending || label.trim() === ""}>
            <Plus size={16} /> Add source
          </button>
        </div>
      </form>

      <table className="mt-8 w-full">
        <thead>
          <tr className="text-left">
            {["Kind", "Label", "Endpoint", "Last sync", "Status", ""].map((h) => (
              <th key={h} className="section-label py-2">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading && (
            <tr><td colSpan={6}><div className="skeleton h-12 my-2" /></td></tr>
          )}
          {data?.sources.length === 0 && !isLoading && (
            <tr>
              <td colSpan={6} className="text-parchment py-10 text-center font-display tracking-smallcaps">
                No sources configured yet — add one above to begin ingesting.
              </td>
            </tr>
          )}
          {data?.sources.map((s) => (
            <tr key={s.id} className="border-t border-hairline">
              <td className="py-3 pr-2 text-parchment">{s.kind}</td>
              <td className="py-3 pr-2 text-ivory">{s.label}</td>
              <td className="py-3 pr-2 text-parchment font-mono text-xs truncate max-w-[420px]">{s.url ?? "—"}</td>
              <td className="py-3 pr-2 text-parchment">
                {s.last_synced_at ? new Date(s.last_synced_at).toLocaleString() : "—"}
              </td>
              <td className="py-3 pr-2">
                {s.last_error
                  ? <span className="text-vermilionHi text-sm">{s.last_error.slice(0, 40)}</span>
                  : <span className="text-jade text-sm">ok</span>}
              </td>
              <td className="py-3 text-right whitespace-nowrap">
                <button className="pill-ghost" onClick={() => refresh.mutate(s.id)} title="Refresh now">
                  <RefreshCw size={14} />
                </button>
                <button className="pill-ghost" onClick={() => remove.mutate(s.id)} title="Remove">
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function YouTubeAccountPanel(): JSX.Element {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const { data, isLoading } = useQuery({
    queryKey: ["youtube-account"],
    queryFn: () => Api.youtubeAccount.status(),
    refetchOnWindowFocus: true,
  });

  // On return from Google OAuth callback we land here with ?yt_connected=1
  // or ?yt_error=... — refetch status and clear the param.
  useEffect(() => {
    if (params.get("yt_connected") === "1" || params.get("yt_error")) {
      void qc.invalidateQueries({ queryKey: ["youtube-account"] });
      const next = new URLSearchParams(params);
      next.delete("yt_connected");
      next.delete("yt_error");
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ytError = params.get("yt_error");

  const disconnect = useMutation({
    mutationFn: () => Api.youtubeAccount.disconnect(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["youtube-account"] }),
  });
  const refresh = useMutation({
    mutationFn: () => Api.youtubeAccount.refresh(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sources"] }),
  });

  function startOAuth(): void {
    // /api/* paths are bearer-gated; include the bearer in a query string so
    // the redirect endpoint passes the auth hook. The hook reads Authorization
    // header OR token query param.
    const t = getBearer() ?? "";
    window.location.href = `/api/youtube/oauth/start?token=${encodeURIComponent(t)}`;
  }

  return (
    <div className="card p-5 mb-6">
      <div className="flex items-center gap-4">
        <Youtube size={32} className="text-vermilionHi" />
        <div className="flex-1">
          <div className="section-label">YouTube account (OAuth)</div>
          {isLoading ? (
            <div className="skeleton h-5 mt-2 w-48" />
          ) : data?.connected ? (
            <div className="mt-1">
              <div className="text-ivory">{data.channel_title ?? "(unknown channel)"} <span className="text-parchment text-sm">connected</span></div>
              <div className="text-parchment text-xs mt-1">
                channel <code className="text-gold">{data.channel_id}</code> · scope <code className="text-gold">{data.scope?.split("/").pop() ?? "?"}</code>
              </div>
            </div>
          ) : (
            <div className="text-parchment mt-1">
              Not connected. Connecting will let wallace-tv pull your subscriptions and liked videos.
            </div>
          )}
          {ytError && (
            <div className="text-vermilionHi text-sm mt-2">OAuth error: {ytError}</div>
          )}
        </div>
        <div className="flex gap-2">
          {!data?.connected ? (
            <button className="pill" onClick={startOAuth}>
              <Link2 size={14} /> Connect
            </button>
          ) : (
            <>
              <button className="pill-ghost" onClick={() => refresh.mutate()} disabled={refresh.isPending}>
                <RefreshCw size={14} /> Sync now
              </button>
              <button className="pill-ghost" onClick={() => disconnect.mutate()} disabled={disconnect.isPending}>
                <Unlink size={14} /> Disconnect
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function RealDebridPanel(): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ["debrid-account"],
    queryFn: () => Api.debrid.account(),
    refetchOnWindowFocus: true,
    staleTime: 60_000,
  });
  return (
    <div className="card p-5 mb-6">
      <div className="flex items-center gap-4">
        <Cloud size={32} className="text-jade" />
        <div className="flex-1">
          <div className="section-label">Real-Debrid (via Torrentio)</div>
          {isLoading ? (
            <div className="skeleton h-5 mt-2 w-48" />
          ) : data?.connected ? (
            <div className="mt-1">
              <div className="text-ivory">
                {data.username ?? "(account)"}
                <span className="text-parchment text-sm"> · {data.type ?? "premium"}</span>
              </div>
              <div className="text-parchment text-xs mt-1">
                {typeof data.premium_days === "number" && (
                  <>{data.premium_days} premium days left</>
                )}
                {data.expiration && (
                  <> · expires <code className="text-gold">{new Date(data.expiration).toLocaleDateString()}</code></>
                )}
              </div>
              <div className="text-parchment/60 text-xs mt-2">
                Acts as an on-demand stream resolver: when a movie or episode has no local
                playable URL, wallace-tv asks Torrentio for cached torrents and proxies
                playback through your Real-Debrid account. Catalog-style live search across
                this library can be enabled per query.
              </div>
            </div>
          ) : (
            <div className="text-parchment mt-1">
              Not connected. Set <code className="text-gold">REAL_DEBRID_TOKEN</code> in{" "}
              <code className="text-gold">/etc/wallace-tv.env</code> and restart the service.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CredentialsPanel(): JSX.Element {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["credentials"],
    queryFn: () => Api.credentials.list(),
    staleTime: 30_000,
  });
  const [editingKey, setEditingKey] = useState<string | null>(null);

  return (
    <div className="card p-5 mb-6">
      <div className="flex items-center gap-3 mb-3">
        <Key size={20} className="text-goldHi" />
        <div className="flex-1">
          <div className="section-label">Credentials</div>
          <div className="text-parchment/70 text-xs mt-1">
            Rotate API keys and tokens here. Values stored in Postgres,
            override <code className="text-gold">/etc/wallace-tv.env</code>{" "}
            at runtime. Changes take effect immediately — no restart needed.
          </div>
        </div>
      </div>

      {q.isLoading && <div className="skeleton h-32" />}
      {q.data && (
        <ul className="mt-2 space-y-2">
          {q.data.credentials.map((c) => {
            const isEditing = editingKey === c.key;
            return (
              <li
                key={c.key}
                className="rounded-md border border-hairline px-4 py-3 hover:bg-elev/30"
              >
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="font-display text-ivory tracking-smallcaps text-sm">
                        {c.label}
                      </span>
                      <code className="text-parchment/50 text-[10px]">{c.key}</code>
                      {c.set ? (
                        <span className="text-jade text-[10px] uppercase tracking-smallcaps font-display">
                          {c.source === "db" ? "rotated" : "from env"}
                        </span>
                      ) : (
                        <span className="text-vermilionHi text-[10px] uppercase tracking-smallcaps font-display">
                          missing
                        </span>
                      )}
                    </div>
                    <div className="text-parchment/60 text-xs mt-0.5">{c.hint}</div>
                    {c.set && c.masked && (
                      <div className="mt-1 font-mono text-parchment text-xs">{c.masked}</div>
                    )}
                    {c.last_used_at && (
                      <div className="mt-1 text-parchment/40 text-[10px]">
                        last used {new Date(c.last_used_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                  {!isEditing && (
                    <div className="flex gap-2">
                      <button
                        className="pill-ghost text-xs"
                        onClick={() => setEditingKey(c.key)}
                      >
                        <Save size={12} /> {c.set ? "Rotate" : "Set"}
                      </button>
                      {c.source === "db" && (
                        <button
                          className="pill-ghost text-xs text-vermilionHi"
                          onClick={() => {
                            if (confirm(`Remove the DB override for ${c.label}? It will fall back to whatever's in /etc/wallace-tv.env.`)) {
                              void Api.credentials.remove(c.key).then(() => qc.invalidateQueries({ queryKey: ["credentials"] }));
                            }
                          }}
                          title="Remove DB override (reverts to env file value)"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {isEditing && (
                  <CredentialEditor
                    credentialKey={c.key}
                    label={c.label}
                    onDone={() => {
                      setEditingKey(null);
                      void qc.invalidateQueries({ queryKey: ["credentials"] });
                    }}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function CredentialEditor({
  credentialKey,
  label,
  onDone,
}: {
  credentialKey: string;
  label: string;
  onDone: () => void;
}): JSX.Element {
  const [value, setValue] = useState("");
  const save = useMutation({
    mutationFn: () => Api.credentials.save(credentialKey, value.trim()),
    onSuccess: onDone,
  });
  return (
    <div className="mt-3 flex gap-2">
      <input
        type="password"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={`New value for ${label}`}
        className="flex-1 bg-ink/60 border border-hairline rounded-md px-3 py-2 text-ivory focus:border-gold outline-none font-mono text-sm"
        autoFocus
        onKeyDown={(e) => {
          if (e.key === "Enter" && value.trim()) save.mutate();
          if (e.key === "Escape") onDone();
        }}
      />
      <button
        className="pill"
        disabled={value.trim().length === 0 || save.isPending}
        onClick={() => save.mutate()}
      >
        <Save size={12} /> Save
      </button>
      <button
        className="pill-ghost"
        onClick={onDone}
      >
        <XIcon size={12} />
      </button>
    </div>
  );
}

function ConfigPanel(): JSX.Element {
  const qc = useQueryClient();
  const [importStatus, setImportStatus] = useState<"idle" | "loading" | "ok" | "error">("idle");
  const [importMsg, setImportMsg] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  async function handleExport(): Promise<void> {
    try {
      const cfg = await Api.config.export();
      const blob = new Blob([JSON.stringify(cfg, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wallace-config-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Export failed: " + String(err));
    }
  }

  async function handleImport(file: File): Promise<void> {
    setImportStatus("loading");
    setImportMsg("");
    try {
      const text = await file.text();
      const config = JSON.parse(text) as unknown;
      const result = await Api.config.import(config);
      setImportStatus("ok");
      setImportMsg(`Applied. ${result.log.length} actions taken.`);
      void qc.invalidateQueries();          // refresh everything
    } catch (err) {
      setImportStatus("error");
      setImportMsg(String(err));
    }
  }

  return (
    <section className="card p-5 mt-6">
      <h2 className="section-label flex items-center gap-2 mb-4">
        <Key size={13} className="text-gold" />
        Config Backup
      </h2>
      <p className="text-parchment/70 text-sm mb-5">
        Export all credentials, sources, and profiles to a JSON file. Import it on a fresh Pi
        to restore everything automatically — or drop the file at{" "}
        <code className="text-gold text-xs">/etc/wallace-tv-config.json</code> and it will load on first boot.
      </p>
      <div className="flex gap-3 flex-wrap items-center">
        <button
          onClick={() => void handleExport()}
          className="pill flex items-center gap-2"
        >
          <Download size={14} />
          Export config
        </button>
        <button
          onClick={() => fileRef.current?.click()}
          className="pill flex items-center gap-2"
          disabled={importStatus === "loading"}
        >
          <Upload size={14} />
          {importStatus === "loading" ? "Importing…" : "Import config"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".json,application/json"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleImport(f);
            e.target.value = "";
          }}
        />
        {importStatus === "ok" && (
          <span className="text-emerald-400 text-sm">{importMsg}</span>
        )}
        {importStatus === "error" && (
          <span className="text-vermilionHi text-sm">{importMsg}</span>
        )}
      </div>
    </section>
  );
}

function YouTubeFields({
  queries, setQueries,
  channelIds, setChannelIds,
  maxPerQuery, setMaxPerQuery,
  order, setOrder,
}: {
  queries: string;
  setQueries: (v: string) => void;
  channelIds: string;
  setChannelIds: (v: string) => void;
  maxPerQuery: number;
  setMaxPerQuery: (v: number) => void;
  order: "date" | "relevance" | "viewCount";
  setOrder: (v: "date" | "relevance" | "viewCount") => void;
}): JSX.Element {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="section-label">Search queries (one per line)</label>
        <textarea
          value={queries}
          onChange={(e) => setQueries(e.target.value)}
          rows={5}
          className="mt-2 w-full bg-ink/60 border border-hairline rounded-md px-3 py-2 text-ivory focus:border-gold outline-none font-mono text-sm"
          placeholder={"history of the silk road\naviation accident analysis\nbritish woodworking"}
        />
        <div className="mt-1 text-xs text-parchment/70">
          Each query costs 100 quota units (10,000/day on a free API key). 25 results per query by default.
        </div>
      </div>
      <div>
        <label className="section-label">Channel IDs (one per line)</label>
        <textarea
          value={channelIds}
          onChange={(e) => setChannelIds(e.target.value)}
          rows={5}
          className="mt-2 w-full bg-ink/60 border border-hairline rounded-md px-3 py-2 text-ivory focus:border-gold outline-none font-mono text-sm"
          placeholder={"UCsXVk37bltHxD1rDPwtNM8Q\nUC1uTbqJu07JJjbsq0bM58vQ"}
        />
        <div className="mt-1 text-xs text-parchment/70">
          Find a channel's ID via View Page Source → search <code className="text-gold">channelId</code>.
        </div>
      </div>
      <div>
        <label className="section-label">Max per query</label>
        <input
          type="number"
          min={1}
          max={50}
          value={maxPerQuery}
          onChange={(e) => setMaxPerQuery(Number(e.target.value))}
          className="mt-2 w-32 bg-ink/60 border border-hairline rounded-md px-3 py-2 text-ivory focus:border-gold outline-none"
        />
      </div>
      <div>
        <label className="section-label">Order</label>
        <select
          value={order}
          onChange={(e) => setOrder(e.target.value as "date" | "relevance" | "viewCount")}
          className="mt-2 w-48 bg-ink/60 border border-hairline rounded-md px-3 py-2 text-ivory focus:border-gold outline-none"
        >
          <option value="date">Date (newest first)</option>
          <option value="relevance">Relevance to query</option>
          <option value="viewCount">View count</option>
        </select>
      </div>
    </div>
  );
}
